import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RotateCcw, Plus } from 'lucide-react';
import api from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { useUIStore } from '@/stores/ui-store';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import Card from '@/components/ui/Card';
import Select from '@/components/ui/Select';
import Textarea from '@/components/ui/Textarea';
import Modal from '@/components/ui/Modal';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

interface ReturnRequest {
  id: string;
  order_id: string;
  order_number: string;
  status: string;
  reason: string;
  customer_notes: string | null;
  created_at: string;
  items: Array<{ product_name: string; quantity: number }> | null;
}

interface DeliveredOrder {
  id: string;
  order_number: string;
  items: Array<{ id: string; product_name: string; variant_name: string | null; quantity: number }> | null;
}

const STATUS_BADGES: Record<string, 'yellow' | 'blue' | 'green' | 'red' | 'gray'> = {
  requested: 'yellow',
  approved: 'blue',
  rejected: 'red',
  label_sent: 'blue',
  received: 'blue',
  refunded: 'green',
  closed: 'gray',
};

const REASON_KEYS = [
  { key: 'wrongItem', value: 'Wrong item received' },
  { key: 'damaged', value: 'Item damaged' },
  { key: 'notAsDescribed', value: 'Item not as described' },
  { key: 'changedMind', value: 'Changed my mind' },
  { key: 'other', value: 'Other' },
] as const;

export default function AccountReturns() {
  const { t } = useTranslation('account');
  const addToast = useUIStore(s => s.addToast);
  const [returns, setReturns] = useState<ReturnRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // New return form
  const [showNewReturn, setShowNewReturn] = useState(false);
  const [deliveredOrders, setDeliveredOrders] = useState<DeliveredOrder[]>([]);
  const [selectedOrderId, setSelectedOrderId] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const reasonOptions = useMemo(
    () => [
      { label: t('returns.selectReason'), value: '' },
      ...REASON_KEYS.map(({ key, value }) => ({
        label: t(`returns.reasons.${key}`),
        value,
      })),
    ],
    [t],
  );

  useEffect(() => { fetchReturns(); }, []);

  async function fetchReturns() {
    setIsLoading(true);
    try {
      const res = await api.get<{ returns: ReturnRequest[] }>('/returns');
      setReturns(res.returns);
    } catch { /* ignore */ } finally { setIsLoading(false); }
  }

  async function openNewReturn() {
    try {
      const res = await api.get<{ orders: DeliveredOrder[] }>('/orders?status=delivered&limit=50');
      setDeliveredOrders(res.orders || []);
    } catch { /* ignore */ }
    setShowNewReturn(true);
  }

  async function handleSubmitReturn() {
    if (!selectedOrderId || !reason) return;
    setIsSubmitting(true);
    try {
      await api.post('/returns', {
        order_id: selectedOrderId,
        reason,
        customer_notes: notes || undefined,
      });
      addToast(t('returns.submitted'), 'success');
      setShowNewReturn(false);
      setSelectedOrderId('');
      setReason('');
      setNotes('');
      fetchReturns();
    } catch (err: any) {
      addToast(err.message || t('returns.submitFailed'), 'error');
    } finally { setIsSubmitting(false); }
  }

  if (isLoading) return <LoadingSpinner className="py-16" />;

  const statusLabel = (status: string): string =>
    t(`returns.status.${status}`, { defaultValue: status });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-display font-bold text-ink">{t('returns.heading')}</h2>
        <Button variant="primary" size="sm" onClick={openNewReturn}>
          <Plus className="h-4 w-4 mr-1" />{t('returns.requestReturn')}
        </Button>
      </div>

      {returns.length === 0 ? (
        <Card>
          <div className="text-center py-8">
            <RotateCcw className="h-10 w-10 text-ink-muted mx-auto mb-3" />
            <p className="text-ink-muted">{t('returns.emptyHeading')}</p>
            <p className="text-sm text-ink-muted mt-1">{t('returns.emptyDescription')}</p>
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          {returns.map(ret => (
            <Card key={ret.id}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Link to={`/account/orders/${ret.order_id}`} className="text-sm font-medium text-sage-700 hover:underline">
                      {t('returns.orderHeading', { number: ret.order_number })}
                    </Link>
                    <Badge variant={STATUS_BADGES[ret.status] || 'gray'}>{statusLabel(ret.status)}</Badge>
                  </div>
                  <p className="text-sm text-ink-soft">{ret.reason}</p>
                  {ret.customer_notes && <p className="text-xs text-ink-muted mt-1">{ret.customer_notes}</p>}
                  {ret.items && ret.items.length > 0 && (
                    <div className="mt-2 text-xs text-ink-muted">
                      {ret.items.map((i, idx) => (
                        <span key={idx}>{idx > 0 && ', '}{i.product_name} x{i.quantity}</span>
                      ))}
                    </div>
                  )}
                </div>
                <span className="text-xs text-ink-muted shrink-0">{formatDate(ret.created_at)}</span>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* New Return Modal */}
      <Modal isOpen={showNewReturn} onClose={() => setShowNewReturn(false)} title={t('returns.modalTitle')}>
        <div className="space-y-4">
          <Select
            label={t('returns.orderLabel')}
            value={selectedOrderId}
            onChange={e => setSelectedOrderId(e.target.value)}
            options={[
              { label: t('returns.selectOrder'), value: '' },
              ...deliveredOrders.map(o => ({
                label: t('returns.orderOption', { number: o.order_number }),
                value: o.id,
              })),
            ]}
          />
          <Select
            label={t('returns.reasonLabel')}
            value={reason}
            onChange={e => setReason(e.target.value)}
            options={reasonOptions}
          />
          <Textarea
            label={t('returns.notesLabel')}
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={3}
            placeholder={t('returns.notesPlaceholder')}
          />

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setShowNewReturn(false)}>{t('returns.cancel')}</Button>
            <Button variant="primary" onClick={handleSubmitReturn}
              isLoading={isSubmitting} disabled={!selectedOrderId || !reason}>
              {t('returns.submit')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
